import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MessageCircle, ArrowRight, Calendar, Newspaper, Share2, Download, ChevronRight, Sparkles } from 'lucide-react';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/logo.svg";




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

function PressCard({ release }) {
  const dateStr = release.published_at 
    ? new Date(release.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) 
    : 'Recent';
    
  return (
    <Link to={"/press/" + release.slug} className="block group">
      <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
        <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
          <Newspaper size={14} />
          <span>Press Release</span>
          <span className="mx-2">•</span>
          <Calendar size={14} />
          <span>{dateStr}</span>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#2C39B8] transition-colors">
          {release.headline || release.title}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-2">{release.subheadline}</p>
        <div className="flex items-center text-[#2C39B8] text-sm font-medium mt-4">
          Read more <ChevronRight size={16} />
        </div>
      </div>
    </Link>
  );
}

export function PressListPage() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReleases() {
      try {
        const res = await fetch(API_URL + '/api/seo/press');
        const data = await res.json();
        setReleases(data.releases || []);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchReleases();
  }, []);

  return (
    <div data-testid="press-list-page">
      <Navbar />
      <SEOHead title="Press - ChannelLoyalty News & Media" description="Latest news, press releases, and media coverage about ChannelLoyalty's B2B channel loyalty platform, product launches, and enterprise milestones." keywords="ChannelLoyalty press releases, B2B loyalty news, channel incentive platform updates" canonical="https://channelloyalty.ai/press" />
      
      <main>
        <section className="hero-full" style={{ paddingBottom: '60px' }}>
          <div className="container">
            <div className="text-center pt-8 max-w-4xl mx-auto">
              <div className="hero-badge mx-auto mb-6">
                <Sparkles size={16} />
                Press Room
              </div>
              <h1 className="text-white mb-6">Press Releases</h1>
              <p className="text-white/80 text-lg">
                Latest news and announcements from ChannelLoyalty.
              </p>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            {loading ? (
              <div className="text-center py-20">
                <div className="animate-spin w-8 h-8 border-4 border-[#2C39B8] border-t-transparent rounded-full mx-auto mb-4"></div>
              </div>
            ) : releases.length === 0 ? (
              <div className="text-center py-20">
                <Newspaper size={48} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Coming Soon</h3>
                <p className="text-gray-500">Press releases will be published here.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {releases.map(function(release) {
                  return <PressCard key={release.slug} release={release} />;
                })}
              </div>
            )}
            
            {/* Media Kit Section */}
            <div className="mt-16 bg-gradient-to-br from-blue-50 to-blue-50 rounded-2xl p-8 border border-blue-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Media Kit</h2>
              <p className="text-gray-600 mb-6">Download our official brand assets and company information.</p>
              <div className="flex flex-wrap gap-4">
                <button className="btn btn-secondary">
                  <Download size={16} /> Logo Pack
                </button>
                <button className="btn btn-secondary">
                  <Download size={16} /> Company Fact Sheet
                </button>
                <button className="btn btn-secondary">
                  <Download size={16} /> Executive Bios
                </button>
              </div>
            </div>
            
            {/* Media Contact */}
            <div className="mt-8 bg-white rounded-2xl p-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Media Contact</h2>
              <p className="text-gray-600 mb-4">For press inquiries, please contact:</p>
              <div className="space-y-2">
                <p className="text-gray-700"><strong>Chandra Shekhar</strong></p>
                <p className="text-gray-600">Email: <a href="mailto:chandra@channelloyalty.ai" className="text-[#2C39B8] hover:underline">chandra@channelloyalty.ai</a></p>
                <p className="text-gray-600">Phone: +91 9910059861</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
    </div>
  );
}

export function PressDetailPage() {
  const params = useParams();
  const slug = params.slug;
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchRelease() {
      try {
        const res = await fetch(API_URL + '/api/seo/press/' + slug);
        if (res.ok) {
          const data = await res.json();
          setRelease(data);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchRelease();
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="press-detail-page">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#2C39B8] border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (!release) {
    return (
      <div data-testid="press-detail-page">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Press Release Not Found</h1>
            <Link to="/press" className="btn btn-primary">View All Press Releases</Link>
          </div>
        </div>
        <MegaFooter />
      </div>
    );
  }

  const dateStr = release.published_at 
    ? new Date(release.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) 
    : '';

  return (
    <div data-testid="press-detail-page">
      <Navbar />
      
      <main>
        <section className="hero-full" style={{ paddingBottom: '40px' }}>
          <div className="container">
            <div className="max-w-4xl mx-auto pt-8">
              <Link to="/press" className="inline-flex items-center text-white/70 hover:text-white mb-6">
                <ChevronRight size={16} className="rotate-180 mr-1" /> Back to Press Room
              </Link>
              <div className="text-white/70 text-sm mb-4">PRESS RELEASE • {dateStr}</div>
              <h1 className="text-white text-3xl md:text-4xl mb-4">{release.headline}</h1>
              {release.subheadline && (
                <p className="text-white/80 text-lg">{release.subheadline}</p>
              )}
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              {/* Dateline */}
              <p className="text-gray-600 font-semibold mb-6">{release.dateline}</p>
              
              {/* Body */}
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed mb-8 whitespace-pre-line">
                {release.body}
              </div>
              
              {/* Boilerplate */}
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-3">About ChannelLoyalty</h3>
                <p className="text-gray-600 whitespace-pre-line">{release.boilerplate}</p>
              </div>
              
              {/* Contact */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Media Contact</h3>
                <p className="text-gray-600 whitespace-pre-line">{release.contact}</p>
              </div>
              
              {/* Share */}
              <div className="mt-8 flex items-center gap-4">
                <span className="text-gray-500 text-sm">Share this release:</span>
                <button 
                  onClick={function() { 
                    if (navigator.share) {
                      navigator.share({ title: release.headline, url: window.location.href });
                    }
                  }}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
                >
                  <Share2 size={18} />
                </button>
                <button 
                  onClick={function() {
                    navigator.clipboard.writeText(release.full_text || '');
                    alert('Press release copied to clipboard!');
                  }}
                  className="btn btn-secondary text-sm"
                >
                  Copy Full Text
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
    </div>
  );
}

export default PressListPage;
